import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Check, ChevronDown } from 'lucide-react';

/**
 * Reusable custom listbox (not OS `<select>` chrome).
 *
 * @param {object} props
 * @param {Array<{ value: string, label: React.ReactNode, disabled?: boolean }>} props.options
 * @param {string} [props.value]
 * @param {(next: string) => void | ((e: { target: { name?: string, value: string } }) => void)} props.onChange
 * @param {string} [props.name] — when set, onChange also receives synthetic event for form handlers
 * @param {string} [props.id]
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props['aria-label']]
 */
const Listbox = forwardRef(function Listbox(
  {
    className = '',
    options: optionsProp = [],
    value = '',
    onChange,
    name,
    id,
    placeholder = 'Select…',
    disabled = false,
    'aria-label': ariaLabel,
    ...rest
  },
  ref
) {
  const options = useMemo(
    () =>
      (optionsProp || []).map((opt) => ({
        value: opt.value == null ? '' : String(opt.value),
        label: opt.label,
        disabled: Boolean(opt.disabled),
      })),
    [optionsProp]
  );

  const selectable = useMemo(
    () => options.filter((opt) => opt.value !== ''),
    [options]
  );
  const placeholderLabel =
    options.find((opt) => opt.value === '')?.label || placeholder;

  const selected = selectable.find((opt) => opt.value === value);
  const empty = !selected;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const autoId = useId();
  const listboxId = `${id || autoId}-listbox`;
  const buttonId = id || autoId;

  const emitChange = useCallback(
    (nextValue) => {
      if (!onChange) return;
      if (name != null && name !== '') {
        onChange({
          target: { name, value: nextValue },
          currentTarget: { name, value: nextValue },
        });
      } else {
        onChange(nextValue);
      }
    },
    [name, onChange]
  );

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    const idx = Math.max(
      0,
      selectable.findIndex((opt) => opt.value === value)
    );
    setActiveIndex(idx === -1 ? 0 : idx);
    setOpen(true);
  }, [disabled, selectable, value]);

  const toggle = useCallback(() => {
    if (open) close();
    else openMenu();
  }, [open, close, openMenu]);

  const choose = useCallback(
    (opt) => {
      if (opt.disabled) return;
      emitChange(opt.value);
      close();
    },
    [emitChange, close]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-option-index="${activeIndex}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const onButtonKeyDown = (event) => {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!open) openMenu();
        else {
          setActiveIndex((prev) => {
            const len = selectable.length;
            if (!len) return -1;
            if (event.key === 'ArrowDown') return (prev + 1) % len;
            return (prev - 1 + len) % len;
          });
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!open) openMenu();
        else if (activeIndex >= 0 && selectable[activeIndex]) {
          choose(selectable[activeIndex]);
        }
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(Math.max(0, selectable.length - 1));
        }
        break;
      default:
        break;
    }
  };

  const setRefs = (node) => {
    rootRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const {
    multiple: _multiple,
    size: _size,
    ...buttonRest
  } = rest;

  return (
    <div className={`relative w-full ${className}`.trim()} ref={setRefs}>
      <button
        type="button"
        id={buttonId}
        name={name}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        onClick={toggle}
        onKeyDown={onButtonKeyDown}
        className={[
          'input-field flex w-full items-center justify-between gap-3 text-left cursor-pointer',
          empty ? 'text-critic-text-muted' : 'text-critic-text-primary',
          open ? 'bg-critic-bg-card/95 border-apple-blue ring-2 ring-apple-blue/30' : '',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...buttonRest}
      >
        <span className="truncate font-normal">
          {selected ? selected.label : placeholderLabel}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-critic-text-muted transition-transform duration-150 ease-out ${
            open ? 'rotate-180' : ''
          }`}
          strokeWidth={2.25}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-labelledby={buttonId}
          tabIndex={-1}
          className="absolute z-50 mt-2 max-h-64 w-full overflow-auto rounded-2xl border border-white/[0.1] bg-critic-bg-card/95 backdrop-blur-xl py-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.45)] animate-listbox-in"
        >
          {selectable.map((opt, index) => {
            const isSelected = opt.value === value;
            const isActive = index === activeIndex;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                data-option-index={index}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(opt)}
                className={[
                  'mx-1.5 flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors duration-100',
                  opt.disabled ? 'opacity-40 cursor-not-allowed' : '',
                  isActive || isSelected
                    ? 'bg-apple-blue/20 text-white'
                    : 'text-critic-text-secondary hover:bg-white/[0.06] hover:text-critic-text-primary',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && (
                  <Check className="h-4 w-4 shrink-0 text-apple-blue" strokeWidth={2.5} aria-hidden />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});

export default Listbox;
